import React, { useState, useMemo, useRef, useCallback, Dispatch } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input
} from 'reactstrap';
import 'react-quill/dist/quill.snow.css';

import imageResize from 'quill-image-resize-module-react';
Quill.register('modules/imageResize', imageResize);

const icons = Quill.import('ui/icons');
icons['delete-image'] = `
<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 462.54"><path fill-rule="nonzero" d="M49.68 0h337.29c13.65 0 26.06 5.64 35.03 14.61l.83.91c8.53 8.94 13.8 21.05 13.8 34.17V192.3c-13.82-3.96-28.42-6.1-43.52-6.1-7.25 0-14.38.5-21.37 1.45l38.17-35.52V49.69c0-5.95-2.36-11.49-6.2-15.62l-.6-.56c-4.16-4.16-9.89-6.79-16.14-6.79H49.68c-6.05 0-11.55 2.35-15.62 6.14l-.58.62c-4.17 4.16-6.76 9.91-6.76 16.21v177.93c29.58-26.67 76.92-63.95 106.82-89.08 4.88-4.22 12.14-4.13 16.93-.04.97.81 1.69 1.79 2.5 2.75l69.64 105.62 24.89-75.76c1.99-10.58 14.48-14.09 22.06-7.06l45.01 43.1c-7.78 4.48-15.13 9.62-21.99 15.32l-23.17-22.24-25.4 78.78c-1.6 11.15-15.44 15.38-22.92 6.78l-80.27-118.34-114.1 95.68v79.68c0 6.26 2.62 11.99 6.79 16.15 4.19 4.2 9.95 6.82 16.17 6.82h187.53c1.3 9.18 3.38 18.11 6.19 26.72H49.68c-13.58 0-26.04-5.62-35.07-14.64C5.64 368.88 0 356.46 0 342.81V49.69c0-13.67 5.59-26.11 14.58-35.11l.92-.83C24.44 5.25 36.49 0 49.68 0zm375.08 299.72 12.28 12.27c2.87 2.88 2.87 7.71 0 10.58l-21.06 21.06 21.06 21.07c2.88 2.88 2.88 7.7 0 10.58l-12.28 12.28c-2.87 2.87-7.66 2.91-10.58 0l-21.06-21.07-21.08 21.08c-2.88 2.88-7.66 2.91-10.58 0l-12.27-12.28c-2.92-2.91-2.92-7.67 0-10.58l21.07-21.07-21.06-21.07c-2.91-2.91-2.91-7.67 0-10.58l12.28-12.28c2.91-2.91 7.7-2.88 10.58 0l21.06 21.07 21.06-21.06c2.91-2.91 7.73-2.85 10.58 0zm-31.65-74.97c32.83 0 62.56 13.31 84.06 34.82 21.52 21.52 34.83 51.24 34.83 84.07 0 32.83-13.31 62.57-34.82 84.07-21.51 21.52-51.24 34.83-84.07 34.83-32.83 0-62.56-13.31-84.07-34.83-21.51-21.5-34.82-51.24-34.82-84.07 0-32.8 13.31-62.5 34.82-84.02 21.56-21.56 51.27-34.87 84.07-34.87zm67.83 51.05c-17.34-17.34-41.34-28.09-67.83-28.09-26.51 0-50.51 10.73-67.86 28.07-17.35 17.35-28.07 41.36-28.07 67.86 0 26.49 10.74 50.5 28.09 67.84 17.35 17.36 41.35 28.1 67.84 28.1 26.49 0 50.49-10.74 67.84-28.1 17.34-17.34 28.09-41.35 28.09-67.84 0-26.48-10.75-50.48-28.1-67.84zM267.9 61.14c11.03 0 21.06 4.48 28.31 11.73s11.73 17.26 11.73 28.31c0 11.03-4.48 21.06-11.73 28.31s-17.28 11.73-28.31 11.73c-11.05 0-21.06-4.48-28.31-11.73s-11.73-17.28-11.73-28.31c0-11.05 4.48-21.06 11.73-28.31s17.26-11.73 28.31-11.73zm12.22 27.82a17.281 17.281 0 0 0-12.22-5.05c-4.77 0-9.1 1.94-12.22 5.05a17.281 17.281 0 0 0-5.05 12.22c0 4.77 1.94 9.1 5.05 12.22 3.12 3.11 7.45 5.05 12.22 5.05 4.77 0 9.1-1.94 12.22-5.05 3.11-3.12 5.05-7.45 5.05-12.22 0-4.77-1.94-9.1-5.05-12.22z"/></svg>
`;

type QuileInput = {
    values: string,
    setValue: Dispatch<string>
    error?: string;
    invalid?: boolean;
    disabled?: boolean
    onBlur?: () => void;
}

const RichTextEditor = ({ values, setValue, error, invalid,onBlur,disabled }: QuileInput) => {
    const quillRef = useRef<ReactQuill>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [imageModal, setImageModal] = useState({
        isOpen: false,
        file: null as File | null,
        previewUrl: '',
        altText: '',
        title: '',
        width: 200,
        height: 'auto' as number | 'auto',
        alignment: 'center' as 'left' | 'center' | 'right',
        keepAspectRatio: true
    });

    const handleImageUpload = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const processImageFile = useCallback((file: File) => {
        const reader = new FileReader();
        reader.onload = (readerEvent) => {
            const img = new Image();
            img.onload = () => {
                setImageModal({
                    isOpen: true,
                    title: imageModal.title,
                    file,
                    previewUrl: readerEvent.target?.result as string,
                    altText: file.name.replace(/\.[^/.]+$/, ''),
                    width: Math.min(img.width, 600),
                    height: img.height * (Math.min(img.width, 600) / img.width),
                    alignment: 'center',
                    keepAspectRatio: true
                });
            };
            img.src = readerEvent.target?.result as string;
        };
        reader.readAsDataURL(file);
    }, []);

    const handleImageResize = useCallback((dimension: 'width' | 'height', value: number) => {
        setImageModal(prev => {
            if (prev.keepAspectRatio) {
                const img = new Image();
                img.src = prev.previewUrl;

                if (dimension === 'width') {
                    const newHeight = (value / img.width) * img.height;
                    return {
                        ...prev,
                        width: value,
                        height: Math.round(newHeight)
                    };
                } else {
                    const newWidth = (value / img.height) * img.width;
                    return {
                        ...prev,
                        height: value,
                        width: Math.round(newWidth)
                    };
                }
            }
            return {
                ...prev,
                [dimension]: value
            };
        });
    }, []);

    const confirmImageUpload = useCallback(() => {
        const { previewUrl, altText, width, height, alignment, title } = imageModal;
        if (!previewUrl) return;

        const quill = quillRef.current?.getEditor();
        const range = quill?.getSelection(true);

        const imgHtml = `
      <img 
        src="${previewUrl}" 
        alt="${altText}" 
        title="${title}"
        width="${width}" 
        ${height !== 'auto' ? `height="${height}"` : ''}
        style="
      display: block; 
      ${alignment === 'center' ? 'margin: 0 auto; display: block;' : ''}
      ${alignment === 'left' ? 'float: left; margin-right: 10px;' : ''}
      ${alignment === 'right' ? 'float: right; margin-left: 10px;' : ''}
    "
  />
    `;

        quill?.clipboard.dangerouslyPasteHTML(range?.index || 0, imgHtml);

        setImageModal({
            isOpen: false,
            title: "",
            file: null,
            previewUrl: '',
            altText: '',
            width: 200,
            height: 'auto',
            alignment: 'center',
            keepAspectRatio: true
        });
    }, [imageModal]);
    
    const deleteImage = useCallback(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        if (!range) return;
        const blot = quill.getLeaf(range.index)[0];
        const imageBlot = blot.domNode.tagName === 'IMG'
            ? blot
            : blot.parent && blot.parent.domNode.tagName === 'IMG'
                ? blot.parent
                : null;

        if (imageBlot) {
            // Remove the entire image blot
            const blotIndex = quill.getIndex(imageBlot);
            quill.deleteText(blotIndex, 1);
        }
    }, []);

    const modules = useMemo(() => ({
        
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'align': [] }],
                ['link', 'image', 'delete-image'],
            ],
            handlers: {
                image: handleImageUpload,
                'delete-image': deleteImage
            }
        },
        imageResize: {
            parchment: Quill.import('parchment'),
            modules: ['Resize', 'DisplaySize', 'Toolbar'],
        }
    }), [handleImageUpload, deleteImage]);

    return (
        <div>
            
            <input
                hidden
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) processImageFile(file);
                }}
            />
            <Modal
                isOpen={imageModal.isOpen}
                toggle={() => setImageModal(prev => ({ ...prev, isOpen: !prev.isOpen }))}
                centered
                size="lg"
            >
                <ModalHeader toggle={() => setImageModal(prev => ({ ...prev, isOpen: false }))}>
                    Insert Image
                </ModalHeader>
                <ModalBody>
                    <div className="d-flex justify-content-center mb-4">
                        <img
                            src={imageModal.previewUrl}
                            alt="Preview"
                            width={imageModal.width}
                            height={imageModal.height !== 'auto' ? imageModal.height : undefined}
                            className="object-contain"
                            style={{ maxWidth: '100%', maxHeight: '400px' }}
                        />
                    </div>
                    <Form>
                        <FormGroup>
                            <Label>Alt Text</Label>
                            <Input
                                type="text"
                                value={imageModal.altText}
                                onChange={(e) => setImageModal(prev => ({
                                    ...prev,
                                    altText: e.target.value
                                }))}
                                placeholder="Describe the Alt Text for the image"
                            />
                        </FormGroup>
                        {/* <FormGroup>
                            <Label>Title Text</Label>
                            <Input
                                type="text"
                                value={imageModal.title}
                                onChange={(e) => setImageModal(prev => ({
                                    ...prev,
                                    title: e.target.value
                                }))}
                                placeholder="Describe the Title for the image"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Width</Label>
                            <Input
                                type="number"
                                value={imageModal.width}
                                onChange={(e) => handleImageResize('width', Number(e.target.value))}
                                min="50"
                                max="800"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Height</Label>
                            <Input
                                type="number"
                                value={imageModal.height !== 'auto' ? imageModal.height : ''}
                                onChange={(e) => handleImageResize('height', Number(e.target.value))}
                                min="50"
                                max="800"
                                disabled={imageModal.keepAspectRatio}
                            />
                        </FormGroup>
                        <FormGroup check>
                            <Input
                                type="checkbox"
                                checked={imageModal.keepAspectRatio}
                                onChange={() => setImageModal(prev => ({
                                    ...prev,
                                    keepAspectRatio: !prev.keepAspectRatio
                                }))}
                            />
                            <Label check>
                                Maintain Aspect Ratio
                            </Label>
                        </FormGroup> */}
                        {/* <FormGroup>
                            <Label>Image Alignment</Label>
                            <div className="d-flex justify-content-between">
                                {['left', 'center', 'right'].map((align) => (
                                    <Button
                                        key={align}
                                        color={imageModal.alignment === align ? 'primary' : 'secondary'}
                                        onClick={() => setImageModal(prev => ({
                                            ...prev,
                                            alignment: align as 'left' | 'center' | 'right'
                                        }))}
                                    >
                                        {align.charAt(0).toUpperCase() + align.slice(1)}
                                    </Button>
                                ))}
                            </div>
                        </FormGroup> */}
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={() => setImageModal(prev => ({ ...prev, isOpen: false }))}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={confirmImageUpload}
                    >
                        Insert Image
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Quill Editor */}
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={values}
                onChange={setValue}
                modules={modules}
                placeholder="Write your blogs here..."
                readOnly={disabled}
                style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                onBlur={() => {
          if (onBlur) onBlur(); // Trigger onBlur if provided
        }}
                // className="min-h-96"
                className={`mb-4 ${invalid ? 'border border-danger' : ''}`}
            />
            {error && <div className="text-danger mb-2">{error}</div>}
        </div>
    );
};

export default RichTextEditor;